// biome-ignore lint/style/useImportType: <explanation>
import { Application, Rectangle } from "pixi.js";

import RenderFactory from "./RenderFactory";

// biome-ignore lint/style/useImportType: <explanation>
import PlayerFactory from "./PlayerFactory";

import type { ISceneDB } from "../../../interfaces/Scenes";

import { compositionConfig } from "./compositionConfig";

import TransitionEngine, {
  DEFAULT_SCENE_TRANSITION,
  type SceneTransition,
  type StingerConfig,
} from "./SceneTransition";

import StingerPlayer from "./StingerPlayer";

/**
 * Mantiene un POOL de escenas vivas (cada una su RenderFactory = su Root container).
 * El pase de escena es O(1): swap de visibilidad (o transición tween sobre los dos
 * Roots), sin teardown+rebuild. Las escenas ocultas se pausan (no consumen GPU/CPU).
 * Cap N + LRU acota la VRAM (las menos usadas se destruyen).
 *
 * Reemplaza al RenderFactory único que tenía PixiFactory. Expone la misma API que
 * PixiFactory usaba (renderSelf vía `active`, switchScene, updateModel, etc.).
 */
export default class SceneCompositor {
  private App: Application;

  private Players: PlayerFactory;

  /** sceneId → layer viva. */
  private Layers = new Map<string, RenderFactory>();

  /** Orden de uso (más reciente al final) para evicción LRU. */
  private Lru: string[] = [];

  private ActiveId: string | null = null;

  private MaxLayers: number;

  private OnLive = false;

  // Tras destroy() el pool queda vacío; los métodos llamados en el teardown
  // (clear/stop/... vía Radio.destroy → destroyPlayers) deben ser no-op, no tirar.
  private Destroyed = false;

  private Transition: TransitionEngine;

  private Stinger: StingerPlayer;

  constructor(
    scene: ISceneDB,
    app: Application,
    players: PlayerFactory,
    // Cap del pool: cuántas escenas se mantienen vivas (con sus fuentes/cámaras
    // abiertas). 3 = activa + 2 recientes → switch-back instantáneo acotando VRAM
    // y hardware abierto. Las demás se reconstruyen on-demand (warmup la 1ª vez).
    maxLayers = 3,
    // AudioContext + destino del mix del broadcast (GlobalAudio), para el audio
    // del stinger. Opcionales (si faltan, el stinger cae a video.muted local).
    audioCtx?: AudioContext,
    audioDest?: AudioNode,
  ) {
    this.App = app;
    this.Players = players;
    this.MaxLayers = Math.max(1, maxLayers);
    this.Transition = new TransitionEngine(
      app,
      compositionConfig.width,
      compositionConfig.height,
    );
    this.Stinger = new StingerPlayer(
      app,
      compositionConfig.width,
      compositionConfig.height,
      audioCtx,
      audioDest,
    );

    // Escena inicial: layer activo, visible, despausado.
    const layer = this.createLayer(scene);
    this.ActiveId = scene.model.id;
    layer.setVisible(true);
    layer.resume();
  }

  /** Layer de la escena activa (PixiFactory.drawFrame renderiza sobre éste). */
  get active(): RenderFactory {
    const layer = this.activeOrNull;
    if (layer) return layer;
    throw new Error("SceneCompositor: no active layer");
  }

  /** Layer activo o null (sin tirar) — usado por los métodos del teardown. */
  private get activeOrNull(): RenderFactory | null {
    const layer = this.ActiveId ? this.Layers.get(this.ActiveId) : undefined;
    if (layer) return layer;
    const first = this.Layers.values().next().value;
    return first ?? null;
  }

  private touchLru(id: string): void {
    const i = this.Lru.indexOf(id);
    if (i >= 0) this.Lru.splice(i, 1);
    this.Lru.push(id);
  }

  private createLayer(scene: ISceneDB): RenderFactory {
    const layer = new RenderFactory(scene, this.App, this.Players);
    layer.setVisible(false); // oculto hasta activarse
    this.Layers.set(scene.model.id, layer);
    this.touchLru(scene.model.id);
    this.evictIfNeeded();
    return layer;
  }

  /** Destruye las escenas menos usadas (nunca la activa) hasta respetar el cap. */
  private evictIfNeeded(): void {
    while (this.Layers.size > this.MaxLayers) {
      const victim = this.Lru.find((id) => id !== this.ActiveId);
      if (!victim) break;
      const layer = this.Layers.get(victim);
      this.Layers.delete(victim);
      this.Lru = this.Lru.filter((id) => id !== victim);
      try {
        // Libera las fuentes de la escena evictada (cámaras/display) antes de
        // destruir su árbol Pixi: si no, las fuentes de escenas que ya no están
        // en el pool quedarían vivas (regresión de recursos vs el destroyPlayers
        // que antes corría en cada pase).
        const areaIds = layer?.getAreaIds() ?? [];
        layer?.destroy();
        for (const areaId of areaIds) {
          this.Players.release(areaId);
        }
      } catch (e) {
        console.warn("[SceneCompositor] evict destroy error:", e);
      }
    }
  }

  private getOrCreateLayer(scene: ISceneDB): {
    layer: RenderFactory;
    reused: boolean;
  } {
    const existing = this.Layers.get(scene.model.id);
    if (existing) {
      this.touchLru(scene.model.id);
      return { layer: existing, reused: true };
    }
    return { layer: this.createLayer(scene), reused: false };
  }

  /**
   * Construye/refresca un layer SIN activarlo (pre-warm F1). updateModel hace el
   * diff (no-op si no cambió, clear+rebuild si cambió); renderSelf monta el árbol.
   * El Root queda visible:false → no se dibuja hasta el switch.
   */
  /** Precarga el asset de un stinger para que el 1er pase no tenga hipo (F5). */
  async prewarmStinger(cfg: StingerConfig): Promise<void> {
    if (this.Destroyed) return;
    await this.Stinger.preload(cfg);
  }

  async prewarm(scene: ISceneDB): Promise<void> {
    if (this.Destroyed) return;
    if (this.Layers.has(scene.model.id)) return; // ya viva
    const layer = this.createLayer(scene);
    await layer.updateModel(scene.model, this.OnLive);
    await layer.renderSelf(this.OnLive);
    // Queda oculta en el pool: sin trabajo per-frame (partículas/procedural/
    // alertas) hasta que un pase la active con resume().
    layer.pause();
  }

  /**
   * Miniatura de una escena SIN pasarla al aire: la pre-construye en el pool (prewarm, queda
   * viva para el próximo pase) y extrae su contenedor con el renderer a un canvas de
   * `width`×`height`. La capa se muestra sólo durante el extract (renderable:false la saltea).
   * `null` si el compositor ya no existe.
   */
  async snapshot(
    scene: ISceneDB,
    width: number,
    height: number,
  ): Promise<HTMLCanvasElement | null> {
    if (this.Destroyed) return null;
    await this.prewarm(scene);
    const layer = this.Layers.get(scene.model.id);
    if (!layer || this.Destroyed) return null;
    const root = layer.getRoot();
    if (root.destroyed) return null;
    // Fuentes de los textos (Roboto) y un frame de ticker para que Pixi asiente el layout.
    await document.fonts.ready;
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
    if (this.Destroyed || root.destroyed) return null;
    const wasVisible = root.visible;
    root.visible = true;
    root.renderable = true;
    try {
      // Solo una capa OCULTA (pausada en el pool) se precalienta: sus
      // partículas/procedural solo pintan dentro del updater y sin esto la
      // miniatura saldría sin efectos. La capa activa ya tiene estado vivo y
      // no debe adelantarse. Dentro del try para que el finally restaure la
      // visibilidad aunque un updater tire.
      if (!wasVisible) layer.warmAnim();
      const source = this.App.renderer.extract.canvas({
        target: root,
        frame: new Rectangle(0, 0, this.App.renderer.width, this.App.renderer.height),
        clearColor: "#000000",
      }) as HTMLCanvasElement;
      const out = document.createElement("canvas");
      out.width = width;
      out.height = height;
      const ctx = out.getContext("2d");
      if (!ctx) return null;
      ctx.drawImage(source, 0, 0, width, height);
      return out;
    } finally {
      root.visible = wasVisible;
      root.renderable = wasVisible;
    }
  }

  /**
   * Pase de escena. Si el layer destino ya está vivo y su modelo no cambió → swap
   * instantáneo (cache hit, no rebuild). Si no existe o cambió → updateModel +
   * renderSelf (reusa el DrawCache por-área). Luego swap de visibilidad o transición.
   */
  async switchScene(
    scene: ISceneDB,
    isStreaming: boolean,
    transition: SceneTransition = DEFAULT_SCENE_TRANSITION,
  ): Promise<void> {
    if (this.Destroyed) return;
    this.OnLive = isStreaming;

    const { layer: toLayer } = this.getOrCreateLayer(scene);
    toLayer.updateOnLive(isStreaming);

    // Construye/refresca el destino OFFSCREEN (sigue visible:false). updateModel
    // decide no-op vs rebuild; renderSelf monta/refresca.
    await toLayer.updateModel(scene.model, isStreaming);
    await toLayer.renderSelf(isStreaming);

    // El "saliente" se captura DESPUÉS del build async: si otro switch se solapó,
    // ActiveId ya refleja la escena realmente activa ahora (evita transicionar
    // desde un layer viejo y dejarlo huérfano visible).
    const fromId = this.ActiveId;
    const fromLayer =
      fromId && fromId !== scene.model.id ? this.Layers.get(fromId) : undefined;

    this.ActiveId = scene.model.id;
    this.touchLru(scene.model.id);
    toLayer.resume();

    // Completa al instante cualquier transición/stinger en curso (de cualquier
    // tipo) antes de arrancar la nueva → sin sprites de stinger pegados ni layers
    // a medio fundir cuando un pase interrumpe a otro.
    this.Transition.cancel();
    this.Stinger.cancel();

    const hideAndPauseFrom = () => {
      if (fromLayer) {
        fromLayer.setVisible(false);
        fromLayer.pause();
      }
    };

    // Stinger: overlay branded que tapa el corte. El entrante queda OCULTO hasta el
    // cut-point; ahí el StingerPlayer dispara el swap (mostrar entrante + ocultar
    // saliente), oculto bajo la animación.
    if (transition.type === "stinger") {
      const cfg = transition.stinger;
      if (cfg && fromLayer) {
        await this.Stinger.run(cfg, () => {
          toLayer.setVisible(true);
          hideAndPauseFrom();
        });
        return;
      }
      // Sin asset o sin saliente → corte directo (no romper el pase).
      this.Stinger.cancel();
      toLayer.setVisible(true);
      hideAndPauseFrom();
      return;
    }

    if (!fromLayer || transition.type === "cut") {
      // Swap instantáneo.
      this.Transition.cancel();
      toLayer.setVisible(true);
      hideAndPauseFrom();
      return;
    }

    // Transición: ambos visibles durante la ventana; al terminar se oculta+pausa el saliente.
    toLayer.setVisible(true);
    await this.Transition.run(
      fromLayer.getRoot(),
      toLayer.getRoot(),
      transition,
      hideAndPauseFrom,
    );
  }

  /** Edición del modelo de la escena ACTIVA (no es un pase). */
  async updateModel(model: ISceneDB["model"]): Promise<void> {
    if (this.Destroyed) return;
    await this.activeOrNull?.updateModel(model, this.OnLive);
  }

  async updateScene(scene: ISceneDB): Promise<void> {
    if (this.Destroyed) return;
    await this.activeOrNull?.updateModel(scene.model, this.OnLive);
  }

  updateOnLive(onLive: boolean): void {
    this.OnLive = onLive;
    for (const layer of this.Layers.values()) {
      layer.updateOnLive(onLive);
    }
  }

  updatePlayers(players: PlayerFactory): void {
    this.Players = players;
  }

  deleteArea(id: string): void {
    this.activeOrNull?.deleteArea(id);
  }

  /** Detiene el dibujo de la escena activa (no destruye el pool). */
  stop(): void {
    this.OnLive = false;
    this.activeOrNull?.stop();
  }

  resume(): void {
    this.activeOrNull?.resume();
  }

  /** Limpia el contenido de la escena activa (compat con sceneDestroy). */
  clear(): void {
    this.activeOrNull?.clear();
  }

  /** Teardown total: destruye todas las escenas vivas. */
  destroy(): void {
    if (this.Destroyed) return;
    this.Destroyed = true;
    this.Transition.cancel();
    this.Stinger.destroy();
    for (const layer of this.Layers.values()) {
      try {
        layer.destroy();
      } catch (e) {
        console.warn("[SceneCompositor] destroy error:", e);
      }
    }
    this.Layers.clear();
    this.Lru = [];
    this.ActiveId = null;
  }
}
